import React, { useState, useMemo, useCallback } from "react"
import {
  Dropdown,
  Spinner,
  Form,
  Button,
  Alert,
} from "react-bootstrap"
import {
  useAllMainSiteContent,
  useCreateMainSiteContent,
  useDeleteMainSiteContent,
  useToggleMainSiteContentStatus,
  useUpdateMainSiteContent,
} from "../../../api/main-site-content/main-site-content.hooks"
import DeleteModal from "../../components/DeleteModal"
import { useUploadFile } from "../../../api/fileUpload/fileUpload.hooks"
import { FiCopy } from "react-icons/fi"
import Pagination from "../../components/Pagination/Pagination"
import _ from "lodash"
import { standardizePageName } from "../../../utils/stringUtils"

// Import section components from BySiteComponents folder
import SectionCard from "./_components/BySiteComponents/SectionCard"
import HeroSectionForm from "./_components/BySiteComponents/HeroSectionForm"
import ExperienceSectionForm from "./_components/BySiteComponents/ExperienceSectionForm"
import ArchitecturalSplendorForm from "./_components/BySiteComponents/ArchitecturalSplendorForm"
import EssentialInformationForm from "./_components/BySiteComponents/EssentialInformationForm"
import TopTouristAttractionsForm from "./_components/BySiteComponents/TopTouristAttractionsForm"
import Section2Form from "./_components/BySiteComponents/Section2Form"
import CategorySelector from "./_components/BySiteComponents/CategorySelector"
import MetaSection from "./_components/BySiteComponents/MetaSection"

const BySite = () => {
  const TYPE = "attractions-by-site"

  const defaultContentStructure = {
    heroSection: { isVisible: true, backgroundImage: "", title: "" },
    experienceSection: {
      isVisible: true,
      heading: "",
      description: "",
      image: "",
    },
    architecturalSplendor: {
      isVisible: true,
      heading: "",
      description: "",
      mainImage: "",
    },
    section_2: {
      isVisible: true,
      heading: "",
      description: "",
      mainImage: "",
    },
    essentialInformation: {
      isVisible: true,
      title: "",
      info: "",
      address: "",
      lat: "",
      lng: "",
    },
    topTouristAttractionsSection: {
      isVisible: true,
      heading: "",
      description: "",
      // backgroundImage: "",
      attractions: [{ title: "", image: "", link: "" }],
    },
    meta: {
      title: "",
      description: "",
    },
  }

  const itemsPerPage = 10
  const [currentPage, setCurrentPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [showFormView, setShowFormView] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [formData, setFormData] = useState({
    siteName: "",
    pageName: "",
    type: "",
    content: defaultContentStructure,
    category: [],
  })
  const [selectedItem, setSelectedItem] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [modalAction, setModalAction] = useState('delete') // 'delete' or 'toggle'
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  })
  const [validationErrors, setValidationErrors] = useState({})

  const { upload, isLoading: isUploading } = useUploadFile()
  const { deleteContent, isLoading: isDeleting } = useDeleteMainSiteContent()
  const {
    content: apiContent,
    isLoading,
    refetch,
  } = useAllMainSiteContent({ type: TYPE })
  const { updateContent, isLoading: isUpdating } = useUpdateMainSiteContent()
  const { createContent, isLoading: isCreating } = useCreateMainSiteContent()
  const { toggleStatus, isLoading: isToggling } = useToggleMainSiteContentStatus()

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant })
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "info" }),
      5000
    )
  }

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      showAlert("Copied to clipboard!", "success")
    } catch (err) {
      showAlert("Failed to copy!", "danger")
    }
  }

  const handleDelete = async () => {
    if (!selectedItem) return
    try {
      let success = false
      if (modalAction === 'delete') {
        success = await deleteContent(selectedItem._id)
        if (success) {
          showAlert("Item deleted successfully!", "success")
        }
      } else if (modalAction === 'toggle') {
        success = await toggleStatus(selectedItem._id)
        if (success) {
          showAlert("Status toggled successfully!", "success")
        }
      }
      
      if (success) {
        setShowDeleteModal(false)
        await refetch()
      }
    } catch {
      showAlert(`Failed to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} item.`, "danger")
    }
  }

  const handleToggleStatus = async (item) => {
    setSelectedItem(item)
    setModalAction('toggle')
    setShowDeleteModal(true)
  }

  const openDeleteModal = (item) => {
    setSelectedItem(item)
    setModalAction('delete')
    setShowDeleteModal(true)
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.pageName.trim()) {
      errors.pageName = "Page name is required"
    }

    Object.entries(formData.content).forEach(([section, fields]) => {
      if (fields.isVisible) {
        if (fields.heading !== undefined && !fields.heading.trim()) {
          errors[`${section}.heading`] = "Heading is required when section is visible"
        }
        // Skip description validation for essentialInformation since it's removed
        if (section !== 'essentialInformation' && fields.description !== undefined && !fields.description.trim()) {
          errors[`${section}.description`] = "Description is required when section is visible"
        }
        if (fields.title !== undefined && !fields.title.trim()) {
          errors[`${section}.title`] = "Title is required when section is visible"
        }
      }
    })

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const sanitizeContentData = (content) => {
    const sanitizedContent = _.cloneDeep(defaultContentStructure)

    Object.keys(sanitizedContent).forEach((sectionKey) => {
      if (content[sectionKey]) {
        const section = content[sectionKey]

        Object.keys(sanitizedContent[sectionKey]).forEach((fieldKey) => {
          if (section[fieldKey] !== undefined) {
            if (Array.isArray(sanitizedContent[sectionKey][fieldKey])) {
              const defaultArrayItem = sanitizedContent[sectionKey][fieldKey][0]
              if (Array.isArray(section[fieldKey])) {
                sanitizedContent[sectionKey][fieldKey] = section[fieldKey].map(
                  (item) => {
                    if (typeof defaultArrayItem === "object" && defaultArrayItem !== null) {
                      const sanitizedItem = {}
                      Object.keys(defaultArrayItem).forEach((itemKey) => {
                        sanitizedItem[itemKey] = item[itemKey] || defaultArrayItem[itemKey]
                      })
                      return sanitizedItem
                    }
                    return item
                  }
                )
              }
            } else {
              sanitizedContent[sectionKey][fieldKey] = section[fieldKey]
            }
          }
        })
      }
    })

    return sanitizedContent
  }

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      showAlert("Please fill in all required fields for visible sections.", "danger")
      return
    }

    try {
      const sanitizedContent = sanitizeContentData(formData.content)

      const payload = {
        siteName: formData.siteName || "main-site",
        pageName: standardizePageName(formData.pageName),
        type: TYPE,
        content: sanitizedContent,
        category: formData.category,
      }   
      let success = false
      if (editItem) {
        success = await updateContent(editItem._id, payload)
      } else {
        success = await createContent(payload)
      }

      if (success) {
        showAlert(
          editItem ? "Updated successfully!" : "Added successfully!",
          "success"
        )
        setShowFormView(false)
        setValidationErrors({})
        await refetch()
      }
    } catch (err) {
      console.error(err)
      showAlert("Operation failed.", "danger")
    }
  }

  const handleEdit = (item) => {
    setEditItem(item)
    const sanitizedContent = sanitizeContentData(item.content || {})

    setFormData({
      siteName: item.siteName || "main-site",
      pageName: item.pageName || "",
      type: item.type || TYPE,
      content: sanitizedContent,
      category: item.category || [],
    })

    setShowFormView(true)
  }

  const handleCategoryChange = useCallback((selections) => {
    setFormData(prev => ({
      ...prev,
      category: selections
    }))
  }, [])

  // Simplified input change handler
  const handleInputChange = (section, key, value) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [section]: { ...prev.content[section], [key]: value },
      },
    }))
  }

  const handleSectionVisibilityChange = (section, isVisible) => {
    handleInputChange(section, "isVisible", isVisible)
  }

  const tableData = useMemo(() => {
    if (!Array.isArray(apiContent)) return []
    return apiContent.map((item) => ({
      id: item._id || item.id,
      siteName: item.siteName || "-",
      pageName: item.pageName || "-",
      type: item.type || "-",
      isActive: item.isActive,
      fullItem: item,
    }))
  }, [apiContent])

  const filtered = tableData.filter((x) =>
    [x.siteName, x.pageName, x.type]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  )

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1
  const paginated = filtered.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  )

  const getSectionTitle = (section) => {
    const titles = {
      heroSection: "Hero Section",
      experienceSection: "About Section",
      architecturalSplendor: "Section 1",
      section_2: "Section 2",
      essentialInformation: "Section 3",
      topTouristAttractionsSection: "Nearby Attractions",
      meta: "SEO Meta Information"
    }
    return titles[section] || section.replace(/([A-Z])/g, " $1")
  }

  const handleMetaUpdate = (metaData) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        meta: metaData,
      },
    }))
  }

  const renderSectionForm = (section, fields) => {
    const commonProps = {
      fields,
      onInputChange: handleInputChange,
      upload,
      showAlert,
      isUploading,
      validationErrors,
      setValidationErrors,
      section
    }

    switch (section) {
      case "heroSection":
        return <HeroSectionForm {...commonProps} />
      case "experienceSection":
        return <ExperienceSectionForm {...commonProps} />
      case "architecturalSplendor":
        return <ArchitecturalSplendorForm {...commonProps} />
      case "section_2":
        return <Section2Form {...commonProps} />
      case "essentialInformation":
        return <EssentialInformationForm {...commonProps} />
      case "topTouristAttractionsSection":
        return <TopTouristAttractionsForm {...commonProps} />
      default:
        return null
    }
  }

  return (
    <div className='col-12'>
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className='mb-3'
        >
          {alert.message}
        </Alert>
      )}

      {!showFormView ? (
        // --- TABLE VIEW ---
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <h4 className='card-title m-0'>By Site</h4>
            <div className='d-flex gap-2'>
              <input
                type='text'
                className='form-control w-auto'
                placeholder='Search content'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                onClick={() => {
                  setEditItem(null)
                  setFormData({
                    siteName: "main-site",
                    pageName: "",
                    type: TYPE,
                    content: defaultContentStructure,
                    category: [], // Changed from 'categories' to 'category'
                  })
                  setShowFormView(true)
                }}
              >
                + Add
              </Button>
            </div>
          </div>

          <div className='card-body'>
            {isLoading ? (
              <div className='d-flex justify-content-center py-5'>
                <Spinner animation='border' />
              </div>
            ) : (
              <>
                <div className='table-responsive'>
                  <table className='table table-striped'>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Site</th>
                        <th>Page</th>
                        <th>Url</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((item, i) => (
                        <tr key={item.id}>
                          <td>{currentPage * itemsPerPage + i + 1}</td>
                          <td>{item.siteName}</td>
                          <td>{item.pageName}</td>
                          <td className='d-flex align-items-center gap-2'>
                            <span>/sites/{item.pageName}</span>
                            <Button
                              variant='link'
                              onClick={() =>
                                handleCopy(`/sites/${item.pageName}`)
                              }
                              title='Copy URL'
                            >
                              <FiCopy />
                            </Button>
                          </td>
                          <td>
                            <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant='link'>
                                &#x2022;&#x2022;&#x2022;
                              </Dropdown.Toggle>
                              <Dropdown.Menu align='end'>
                                <Dropdown.Item
                                  onClick={() => handleEdit(item.fullItem)}
                                >
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleToggleStatus(item.fullItem)}
                                >
                                  {item.fullItem.isActive ? 'Deactivate' : 'Activate'}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => openDeleteModal(item.fullItem)}
                                  className="text-danger"
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  onPageChange={setCurrentPage}
                />

                {filtered.length === 0 && !isLoading && (
                  <div className="text-center py-4">
                    <p className="text-muted">No content found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        // --- FORM VIEW ---
        <div className='card p-4'>
          <div className='d-flex align-items-center mb-4'>
            <Button
              variant='link'
              className='p-0 me-3'
              onClick={() => setShowFormView(false)}
            >
              ← Back
            </Button>
            <h4 className='m-0'>
              {editItem ? "Edit Content" : "Add New Content"}
            </h4>
          </div>

          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>
                Page Name <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                value={formData.pageName}
                onChange={(e) => {
                  setFormData({ ...formData, pageName: e.target.value })
                  if (validationErrors.pageName) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      pageName: undefined,
                    }))
                  }
                }}
                isInvalid={!!validationErrors.pageName}
              />
              {formData.pageName && (
                <div className='form-text'>
                  URL: /sites/{standardizePageName(formData.pageName)}
                </div>
              )}
              {validationErrors.pageName && (
                <Form.Control.Feedback type='invalid'>
                  {validationErrors.pageName}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Add CategorySelector component */}
            <CategorySelector
              onSelectionChange={handleCategoryChange}
              initialSelections={formData.category} // Changed from 'categories' to 'category'
            />

            {Object.entries(formData.content)
              .filter(([section]) => section !== "meta")
              .map(([section, fields]) => (
              <SectionCard
                key={section}
                title={getSectionTitle(section)}
                fields={fields}
                onVisibilityChange={(isVisible) =>
                  handleSectionVisibilityChange(section, isVisible)
                }
              >
                {renderSectionForm(section, fields)}
              </SectionCard>
            ))}

            {/* Meta Section */}
            <MetaSection
              data={formData.content.meta}
              onUpdate={handleMetaUpdate}
            />

            <div className='d-flex justify-content-end gap-2'>
              <Button
                variant='secondary'
                onClick={() => setShowFormView(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={isUpdating || isCreating}
              >
                {isUpdating || isCreating ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    {editItem ? "Saving..." : "Adding..."}
                  </>
                ) : editItem ? (
                  "Save Changes"
                ) : (
                  "Add Content"
                )}
              </Button>
            </div>
          </Form>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isLoading={modalAction === 'delete' ? isDeleting : isToggling}
        title={modalAction === 'delete' ? 'Delete Content' : 'Toggle Status'}
        confirmText={modalAction === 'delete' ? 'Delete Content' : 'Yes'}
        message={
          selectedItem
            ? modalAction === 'delete'
              ? `Are you sure you want to delete "${selectedItem.pageName}"?`
              : `Are you sure you want to ${selectedItem.isActive ? 'deactivate' : 'activate'} "${selectedItem.pageName}"?`
            : `Are you sure you want to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} this item?`
        }
      />
    </div>
  )
}

export default BySite
